import React, { useMemo, useState } from 'react';
import {
  Button,
  Card,
  Drawer,
  Form,
  Input,
  Popconfirm,
  Select,
  Space,
  Tag,
  message,
} from 'antd';
import { BookOutlined, CopyOutlined, DeleteOutlined, ImportOutlined, PlusOutlined, ToolOutlined } from '@ant-design/icons';
import { useMutation, useQuery } from '@tanstack/react-query';
import { epcEstimationService } from '@/services/epcEstimation.service';
import { buildWbsTableTree, type Est3WbsNode } from '@/utils/est3WbsTree';
import { Est3WbsTreeTable } from '@/components/epc/shared/Est3WbsTreeTable';
import { layerLabel, type Est3WbsLayer } from '@/utils/est3WbsLayers';
import { EpcEstimateWbsTreeSelect } from './EpcEstimateWbsTreeSelect';
import { EpcEstimateWbsImportWizard } from './EpcEstimateWbsImportWizard';
import { EpcEstimateWbsGuidedSetupWizard } from './EpcEstimateWbsGuidedSetupWizard';

export interface EpcEstimateWbsWorkbenchProps {
  estimateId: string;
  revisionId: string;
  readOnly?: boolean;
  nodes: Est3WbsNode[];
  loading?: boolean;
  onChanged?: () => void;
  defaultProjectId?: string;
}

export const EpcEstimateWbsWorkbench: React.FC<EpcEstimateWbsWorkbenchProps> = ({
  estimateId,
  revisionId,
  readOnly,
  nodes,
  loading,
  onChanged,
  defaultProjectId,
}) => {
  const [importOpen, setImportOpen] = useState(false);
  const [guidedOpen, setGuidedOpen] = useState(false);
  const [addOpen, setAddOpen] = useState(false);
  const [editNode, setEditNode] = useState<Est3WbsNode | null>(null);
  const [addForm] = Form.useForm();
  const [editForm] = Form.useForm();

  const layersQ = useQuery({
    queryKey: ['epc-wbs-layers', estimateId],
    queryFn: () => epcEstimationService.listWbsLayers(estimateId),
  });
  const layers = (layersQ.data || []) as Est3WbsLayer[];

  const treeData = useMemo(() => buildWbsTableTree(nodes), [nodes]);

  const createM = useMutation({
    mutationFn: (v: {
      code: string;
      name: string;
      parentId?: string;
      wbsLayerCode?: string;
      description?: string;
      shortName?: string;
      referenceScopeCode?: string;
    }) =>
      epcEstimationService.createWbs(estimateId, { revisionId, ...v }),
    onSuccess: () => {
      message.success('WBS node added');
      addForm.resetFields();
      setAddOpen(false);
      onChanged?.();
    },
    onError: (e: any) => message.error(e?.response?.data?.message || e?.message || 'Add failed'),
  });

  const patchM = useMutation({
    mutationFn: (p: { id: string } & Record<string, unknown>) =>
      epcEstimationService.patchWbs(estimateId, p.id, {
        revisionId,
        code: p.code as string,
        name: p.name as string,
        parentId: p.parentId as string | null | undefined,
        wbsLayerCode: p.wbsLayerCode as string | undefined,
        description: p.description as string | null | undefined,
        shortName: p.shortName as string | null | undefined,
        referenceScopeCode: p.referenceScopeCode as string | null | undefined,
      }),
    onSuccess: () => {
      message.success('WBS node updated');
      setEditNode(null);
      onChanged?.();
    },
    onError: (e: any) => message.error(e?.response?.data?.message || e?.message || 'Update failed'),
  });

  const deleteM = useMutation({
    mutationFn: (wbsId: string) => epcEstimationService.deleteWbs(estimateId, wbsId, revisionId),
    onSuccess: () => {
      message.success('WBS node removed');
      setEditNode(null);
      onChanged?.();
    },
    onError: (e: any) => message.error(e?.response?.data?.message || e?.message || 'Delete failed'),
  });

  const downloadDictionary = async () => {
    try {
      const blob = await epcEstimationService.downloadWbsDictionary(estimateId, revisionId);
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = 'est3-wbs-dictionary.xlsx';
      a.click();
      URL.revokeObjectURL(url);
    } catch (e: any) {
      message.error(e?.message || 'Dictionary export failed');
    }
  };

  const layerFields = (
    <>
      <Form.Item name="wbsLayerCode" label="EPC layer">
        <Select
          allowClear
          placeholder="Auto from parent"
          options={layers.map((l) => ({ value: l.code, label: layerLabel(l) }))}
        />
      </Form.Item>
      <Form.Item name="description" label="Description">
        <Input.TextArea rows={2} placeholder="Scope description" />
      </Form.Item>
      <Form.Item name="shortName" label="Short name">
        <Input placeholder="Alias" />
      </Form.Item>
      <Form.Item name="referenceScopeCode" label="Reference scope">
        <Input placeholder="Benchmark / standard code" />
      </Form.Item>
    </>
  );

  return (
    <Card
      size="small"
      title="WBS structure"
      extra={
        <Space size="small" wrap>
          <Button size="small" icon={<BookOutlined />} onClick={() => void downloadDictionary()}>
            Dictionary
          </Button>
          {!readOnly ? (
            <>
              <Button size="small" icon={<ToolOutlined />} onClick={() => setGuidedOpen(true)}>
                Guided setup
              </Button>
              <Button size="small" icon={<ImportOutlined />} onClick={() => setImportOpen(true)}>
                Import WBS
              </Button>
              <Button size="small" icon={<CopyOutlined />} onClick={() => setImportOpen(true)}>
                Copy from project
              </Button>
              <Button size="small" type="primary" icon={<PlusOutlined />} onClick={() => setAddOpen(true)}>
                Add node
              </Button>
            </>
          ) : null}
        </Space>
      }
    >
      <Est3WbsTreeTable
        dataSource={treeData}
        loading={loading}
        readOnly={readOnly}
        onSelect={(row) => {
          if (!readOnly) {
            setEditNode(row);
            editForm.setFieldsValue({
              code: row.code,
              name: row.name,
              parentId: row.parent_id || row.parentId || undefined,
              wbsLayerCode: (row as any).layer_code || undefined,
              description: (row as any).description || undefined,
              shortName: (row as any).short_name || undefined,
              referenceScopeCode: (row as any).reference_scope_code || undefined,
            });
          }
        }}
        extraColumns={[
          {
            title: 'Ref scope',
            width: 100,
            render: (_: unknown, row: any) => row.reference_scope_code || '—',
            ellipsis: true,
          },
          {
            title: 'Active',
            dataIndex: 'is_active',
            width: 70,
            render: (v) =>
              v === false || v === 0 ? <Tag>off</Tag> : <Tag color="green">on</Tag>,
          },
        ]}
      />

      <Drawer
        title="Add WBS node"
        open={addOpen}
        onClose={() => setAddOpen(false)}
        width={440}
        destroyOnClose
        extra={
          <Button type="primary" loading={createM.isPending} onClick={() => addForm.submit()}>
            Add
          </Button>
        }
      >
        <Form form={addForm} layout="vertical" onFinish={(v) => createM.mutate(v)}>
          <Form.Item name="code" label="Code" rules={[{ required: true }]}>
            <Input placeholder="e.g. U01-A1" />
          </Form.Item>
          <Form.Item name="name" label="Name" rules={[{ required: true }]}>
            <Input placeholder="Node name" />
          </Form.Item>
          <Form.Item name="parentId" label="Parent">
            <EpcEstimateWbsTreeSelect nodes={nodes} allowClear placeholder="Root (no parent)" />
          </Form.Item>
          {layerFields}
        </Form>
      </Drawer>

      <Drawer
        title="Edit WBS node"
        open={!!editNode}
        onClose={() => setEditNode(null)}
        width={440}
        extra={
          <Space>
            <Popconfirm
              title="Remove this WBS node?"
              description="Only leaf nodes with no cost references can be removed."
              onConfirm={() => deleteM.mutate(editNode!.id)}
              okText="Remove"
              okButtonProps={{ danger: true }}
            >
              <Button danger icon={<DeleteOutlined />} loading={deleteM.isPending}>
                Remove
              </Button>
            </Popconfirm>
            <Button type="primary" loading={patchM.isPending} onClick={() => editForm.submit()}>
              Save
            </Button>
          </Space>
        }
      >
        <Form
          form={editForm}
          layout="vertical"
          onFinish={(v) => {
            if (!editNode) return;
            patchM.mutate({ id: editNode.id, ...v, parentId: v.parentId ?? null });
          }}
        >
          <Form.Item name="code" label="Code" rules={[{ required: true }]}>
            <Input />
          </Form.Item>
          <Form.Item name="name" label="Name" rules={[{ required: true }]}>
            <Input />
          </Form.Item>
          <Form.Item name="parentId" label="Parent">
            <EpcEstimateWbsTreeSelect
              nodes={nodes.filter((n) => n.id !== editNode?.id)}
              allowClear
              placeholder="Root (no parent)"
            />
          </Form.Item>
          {layerFields}
        </Form>
      </Drawer>

      <EpcEstimateWbsImportWizard
        open={importOpen}
        onClose={() => setImportOpen(false)}
        estimateId={estimateId}
        revisionId={revisionId}
        readOnly={readOnly}
        defaultProjectId={defaultProjectId}
        onImported={() => onChanged?.()}
      />

      <EpcEstimateWbsGuidedSetupWizard
        open={guidedOpen}
        onClose={() => setGuidedOpen(false)}
        estimateId={estimateId}
        revisionId={revisionId}
        readOnly={readOnly}
        existingNodes={nodes as any[]}
        onCompleted={() => onChanged?.()}
      />
    </Card>
  );
};
